import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Map, { Marker, NavigationControl } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import { MapPin } from "lucide-react";
import {
  EDMONTON_COMMUNITIES,
  EDMONTON_MAP_CENTER,
  EDMONTON_MAP_ZOOM,
} from "@/data/communities";

/**
 * Controlled interactive Mapbox map of Edmonton neighbourhoods.
 * Renders the custom style, fits all 11 markers into view on mount, and
 * syncs hover state with the parent's list panel (no floating tooltips
 * on the map itself — the list row is the info display).
 *
 * Props:
 *   hoveredSlug     currently hovered community slug (string | null)
 *   filteredSlugs   Set<string> — markers whose slug is in this Set stay
 *                   fully opaque; the rest fade to 25% opacity.
 *                   Pass null (no filter) to show all at 100%.
 *   onHover(slug)   called on marker hover / focus (or null on leave)
 */

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";

/* Interactive map style
 * ---------------------
 * We use Mapbox's classic Light v11 style rather than the custom
 * "Monochrome" style stored at REACT_APP_MAPBOX_STYLE.
 *
 * Why: the custom style is Standard-v3-based, which loads a heavier
 * worker code path that fetches 3D-model vector tiles
 * (mapbox.mapbox-models-v1). When the production build (Terser +
 * webpack) touches mapbox-gl's already-minified worker, the internal
 * references in that Standard-v3 code path break with
 *   ReferenceError: a is not defined  (loadTileData → loadVectorData)
 * The error is production-only — dev is unminified, so preview works.
 *
 * Light v11 uses the classic tile pipeline (streets-v8-lite +
 * terrain-v3 only, no models). Its worker code path is smaller and
 * doesn't hit the breaking branch, so it renders reliably on prod.
 *
 * Visually Light v11 gives warm cream land + soft blue-grey water,
 * which fits ROAM's palette. If we later want the branded Monochrome
 * look back, that has to be either redesigned in Mapbox Studio as a
 * *classic* (v10/v11-family) style, or the mapbox-gl build issue
 * fixed at the bundler level. */
const STYLE = "mapbox://styles/mapbox/light-v11";

// Deliberate per-marker chip positioning to prevent central-cluster label
// collisions (Westmount/Glenora/Crestwood/Old Strathcona/Bonnie Doon/Ottewell)
// and keep every label readable at default zoom. Each value is a side the
// chip renders on, relative to the marker dot. Supported sides:
//   "top" | "bottom" | "left" | "right" | "top-left" | "top-right" |
//   "bottom-left" | "bottom-right"
// Hardcoded here — tune per-community without touching layout logic.
const LABEL_POSITION = {
  // South + edge markers — vertical placement keeps them off the perimeter
  "mill-woods": "bottom",
  chappelle: "bottom",
  windermere: "left",
  mayfield: "left",
  "forest-heights": "right",
  // Central cluster — explicit per-marker anchoring so no chip touches
  // another chip or another dot at the default fit-bounds zoom.
  westmount: "top",
  glenora: "left",
  grovenor: "top-left",
  crestwood: "bottom-left",
  "old-strathcona": "left",
  "bonnie-doon": "right",
  ottewell: "right",
};

/* Absolute-position CSS for each supported side. Keeps the chip anchored
 * relative to the dot regardless of flex flow, and lets us support
 * diagonals (needed for Crestwood → bottom-left). */
const CHIP_POS = {
  top:            { bottom: "calc(100% + 6px)", left: "50%",  transform: "translateX(-50%)" },
  bottom:         { top:    "calc(100% + 6px)", left: "50%",  transform: "translateX(-50%)" },
  left:           { right:  "calc(100% + 8px)", top:  "50%",  transform: "translateY(-50%)" },
  right:          { left:   "calc(100% + 8px)", top:  "50%",  transform: "translateY(-50%)" },
  "top-left":     { bottom: "calc(100% + 4px)", right:"calc(50% + 6px)" },
  "top-right":    { bottom: "calc(100% + 4px)", left: "calc(50% + 6px)" },
  "bottom-left":  { top:    "calc(100% + 4px)", right:"calc(50% + 6px)" },
  "bottom-right": { top:    "calc(100% + 4px)", left: "calc(50% + 6px)" },
};

export const EdmontonMap = ({
  hoveredSlug = null,
  filteredSlugs = null,
  onHover = () => {},
}) => {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [tappedSlug, setTappedSlug] = useState(null);

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");
    const on = () => setIsMobile(mql.matches);
    on();
    mql.addEventListener("change", on);
    return () => mql.removeEventListener("change", on);
  }, []);

  /* Compute bounds so every marker fits with padding on first load.
   * We inflate the raw bounds slightly (extra south + a touch on all
   * other edges) so the outermost markers — Chappelle in the south,
   * Mayfield/Windermere in the west, Forest Heights in the northeast —
   * never render against the very edge of the map viewport. */
  const bounds = useMemo(() => {
    const lngs = EDMONTON_COMMUNITIES.map((c) => c.lng);
    const lats = EDMONTON_COMMUNITIES.map((c) => c.lat);
    const pad = 0.008;
    return [
      [Math.min(...lngs) - pad, Math.min(...lats) - pad],
      [Math.max(...lngs) + pad, Math.max(...lats) + pad],
    ];
  }, []);

  /* Fit-bounds with responsive padding — small viewports need much less
   * padding or the visible map area collapses to a narrow centre column
   * and markers on the perimeter render outside the canvas. */
  const doFit = () => {
    const map = mapRef.current?.getMap?.();
    if (!map) return;
    // Ensure the mapbox canvas matches the container's current size before
    // we compute fit — this is critical on mobile where the container width
    // can change between initial mount and the first paint.
    map.resize();
    const w = map.getContainer().clientWidth;
    const narrow = w < 640;
    const padding = narrow
      ? { top: 36, right: 24, bottom: 48, left: 24 }
      : { top: 100, right: 80, bottom: 120, left: 80 };
    map.fitBounds(bounds, { padding, duration: 0 });
  };

  /* Re-fit whenever the container resizes (rotate, keyboard open, layout
   * shift, etc.) — first fit is triggered by the <Map onLoad> prop below. */
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => doFit());
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bounds]);

  /* --- graceful fallback when the token hasn't been added yet --- */
  if (!TOKEN) {
    return (
      <div
        className="relative flex h-full min-h-[500px] flex-col items-center justify-center text-center"
        style={{
          borderRadius: 24,
          backgroundColor: "#EDE9E0",
          border: "1px dashed rgba(32,30,26,0.24)",
          padding: "2rem",
        }}
        data-testid="edmonton-map-placeholder"
      >
        <MapPin size={28} color="#C89A4E" strokeWidth={1.4} />
        <p
          className="label-caps mt-4 text-[11px]"
          style={{ color: "#24382E" }}
        >
          Mapbox token missing
        </p>
        <p
          className="mt-3 max-w-[46ch] text-[13px] leading-[1.6]"
          style={{ color: "rgba(32,30,26,0.72)" }}
        >
          Add <code>REACT_APP_MAPBOX_TOKEN</code> and{" "}
          <code>REACT_APP_MAPBOX_STYLE</code> to <code>/app/frontend/.env</code>
          {" "}then restart the frontend.
        </p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative h-full overflow-hidden"
      style={{
        borderRadius: 24,
        minHeight: 500,
        boxShadow: "0 30px 60px -30px rgba(20,35,27,0.35)",
      }}
      data-testid="edmonton-mapbox"
    >
      <Map
        ref={mapRef}
        mapboxAccessToken={TOKEN}
        mapStyle={STYLE}
        initialViewState={{
          longitude: EDMONTON_MAP_CENTER.lng,
          latitude: EDMONTON_MAP_CENTER.lat,
          zoom: EDMONTON_MAP_ZOOM,
        }}
        scrollZoom={false}
        cooperativeGestures={!isMobile}
        onLoad={(e) => {
          // eslint-disable-next-line no-console
          console.info(
            "[EdmontonMap] style loaded:",
            e?.target?.getStyle?.()?.name || STYLE,
          );
          doFit();
        }}
        onError={(e) => {
          // Surface Mapbox style / tile errors instead of silently falling
          // back to the default grey basemap. Common causes: token can't
          // access the style URL (403), style ID typo, or network block.
          // eslint-disable-next-line no-console
          console.error(
            "[EdmontonMap] mapbox error while loading style",
            STYLE,
            e?.error || e,
          );
        }}
        style={{ width: "100%", height: "100%" }}
      >
        <NavigationControl position="top-right" showCompass={false} />

        {EDMONTON_COMMUNITIES.map((c) => {
          const isHovered = hoveredSlug === c.slug;
          const isTapped = isMobile && tappedSlug === c.slug;
          const active = isHovered || isTapped;
          const dimmed = filteredSlugs && !filteredSlugs.has(c.slug);
          const side = LABEL_POSITION[c.slug] || "right";
          const chipPos = CHIP_POS[side] || CHIP_POS.right;
          const showChip = !isMobile || active;
          return (
            <Marker
              key={c.slug}
              longitude={c.lng}
              latitude={c.lat}
              anchor="center"
              style={{ zIndex: active ? 20 : 1 }}
            >
              <button
                type="button"
                onMouseEnter={() => !isMobile && onHover(c.slug)}
                onMouseLeave={() => !isMobile && onHover(null)}
                onFocus={() => onHover(c.slug)}
                onBlur={() => onHover(null)}
                onClick={(e) => {
                  if (isMobile && tappedSlug !== c.slug) {
                    // first tap on mobile: reveal chip; second tap navigates.
                    e.preventDefault();
                    setTappedSlug(c.slug);
                    onHover(c.slug);
                    return;
                  }
                  navigate(`/neighbourhoods/${c.slug}`);
                }}
                aria-label={`${c.name} — ${c.vibe}`}
                data-testid={`map-marker-${c.slug}`}
                className="group relative"
                style={{
                  cursor: "pointer",
                  position: "relative",
                  padding: isMobile ? 10 : 0, // generous tap target on touch
                  margin: isMobile ? -10 : 0,
                  opacity: dimmed ? 0.25 : 1,
                  transition: "opacity 0.3s ease",
                  background: "transparent",
                  border: "none",
                  lineHeight: 0,
                }}
              >
                {/* dot */}
                <motion.span
                  aria-hidden
                  className="block rounded-full"
                  style={{
                    width: isMobile ? 14 : 12,
                    height: isMobile ? 14 : 12,
                    backgroundColor: active ? "#E3B268" : "#C89A4E",
                    border: "2px solid #24382E",
                    boxShadow: "0 0 0 4px rgba(248,245,239,0.8)",
                  }}
                  animate={{ scale: active ? 1.5 : 1 }}
                  transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                />
                {/* chip — absolutely positioned on the assigned side to prevent
                    label collisions in the central cluster. Hidden on mobile
                    until the marker is tapped. */}
                {showChip && (
                  <span
                    className="label-caps whitespace-nowrap rounded-full transition-colors duration-300"
                    style={{
                      position: "absolute",
                      ...chipPos,
                      padding: "3px 8px",
                      fontSize: 10,
                      lineHeight: 1.2,
                      backgroundColor: active ? "#24382E" : "#F8F5EF",
                      color: active ? "#F8F5EF" : "#24382E",
                      border: "1px solid rgba(32,30,26,0.28)",
                      boxShadow: "0 4px 10px -4px rgba(20,35,27,0.3)",
                      pointerEvents: "none",
                    }}
                  >
                    {c.name}
                  </span>
                )}
              </button>
            </Marker>
          );
        })}
      </Map>

      {/* Mobile hint — first-time affordance that dots are tappable */}
      {isMobile && !tappedSlug && (
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 rounded-full px-3 py-1.5"
          style={{
            backgroundColor: "rgba(36,56,46,0.9)",
            color: "#F8F5EF",
            fontSize: 11,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
          }}
        >
          Tap a dot
        </div>
      )}
    </div>
  );
};

export default EdmontonMap;
